import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import FiberManualRecordOutlinedIcon from '@mui/icons-material/FiberManualRecordOutlined'
import { Box, ListItemButton, useTheme } from '@mui/material'
import List from '@mui/material/List'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import { DateTime } from 'luxon'
import { useCallback, useEffect, useRef } from 'react'

import DepartureBoardIcon from '@mui/icons-material/DepartureBoard'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import SensorsIcon from '@mui/icons-material/Sensors'

import { useNavigate } from '@tanstack/react-router'
import { useLocalStorage } from '@uidotdev/usehooks'

import { useTranslation } from 'react-i18next'

import type { FormatType } from 'src/lib/util'
import { formatTime } from 'src/lib/util'

interface Stop {
    gtfs_id: string
    name: string
}

interface StopTime {
    stop: Stop
    sequence: number
    realtime: boolean
    realtime_status: 'CANCELED' | 'UPDATED' | 'SCHEDULED'
    arrival_realtime: number
    departure_realtime: number
    arrival_delay: number | null
}

interface Trip {
    stop_times: StopTime[]
    trip_headsign: string
    color: string
}

interface BusStopProps {
    stop: Stop
    sequence: number
    realtime: boolean
    realtimeStatus: 'CANCELED' | 'UPDATED' | 'SCHEDULED'
    arrivalRealtime: number
    departureRealtime: number
    isLast: boolean
    isFirst: boolean
    color: string
    isPassed: boolean
    stopRef: (el: HTMLDivElement | null) => void
    gtfsId: string
    delay: number | null
    isToday: boolean
    timeFormatPreference: FormatType
}

interface BusTrackerProps {
    tripData: Trip
    isToday: boolean
}

interface TripArrivalsProps {
    tripData: Trip
    isToday?: boolean
}

function formatDelay(secondsArrival: number): string {
    if (Math.abs(secondsArrival) <= 29) {
        return ''
    }
    else if (secondsArrival >= 30) {
        return `+${Math.ceil(secondsArrival / 60)} min`
    }
    else {
        return `-${Math.ceil(Math.abs(secondsArrival) / 60)} min`
    }
}

function formatAbsoluteStopTimeWindow(secondsArrival: number, secondsDeparture: number): string {
    const base = DateTime.local({ zone: 'Europe/Ljubljana' }).startOf('day')
    const arrival = base.plus({ seconds: secondsArrival }).toFormat('HH:mm')
    const departure = base.plus({ seconds: secondsDeparture }).toFormat('HH:mm')

    if (arrival === departure) return arrival
    return `${arrival} - ${departure}`
}

function figureOutIfBusHasPassed(stopTime: StopTime, tripData: Trip, isToday: boolean): boolean {
    if (!isToday) return false
    if ((stopTime as unknown as { passed?: boolean }).passed) return true
    const nowSecondsFromMidnight = DateTime.now().toSeconds() - DateTime.now().startOf('day').toSeconds()
    // iterate over all stops, check if there is a stop that has a realtime departure time, if it's only after the current stop, then the bus has passed
    if (stopTime.realtime && stopTime.departure_realtime > nowSecondsFromMidnight) {
        return false
    }
    else {
        // if the any of the stops after the current stop has a realtime departure time, then the bus has passed
        const index = tripData.stop_times.findIndex(stop => stop.stop.gtfs_id === stopTime.stop.gtfs_id)
        const stopsAfterCurrent = tripData.stop_times.slice(index + 1)
        const hasPassed = stopsAfterCurrent.some(stop => stop.realtime && stop.departure_realtime < nowSecondsFromMidnight)
        if (!hasPassed) {
            // just do a simple time check
            return stopTime.departure_realtime < nowSecondsFromMidnight
        }
        else {
            return true
        }
    }
}

const BusStop = ({
    stop, realtimeStatus, arrivalRealtime, departureRealtime, isLast, isFirst, color, isPassed, stopRef, gtfsId, delay, isToday,
    timeFormatPreference,
}: BusStopProps) => {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const theme = useTheme()

    const clickHandler = (stopId: string) => {
        navigate({
            to: `/stop/${stopId}`,
        })
    }

    return (
        <Box
            ref={stopRef}
            sx={{
                'display': 'flex',
                'flexDirection': 'row',
                'alignItems': 'center',
                'width': '100%',
                'height': '3rem',
                'position': 'relative',
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: '67%',
                    left: '0.72rem',
                    height: isLast ? '0%' : '66%',
                    width: '8px',
                    bgcolor: !isPassed ? color : 'lightgrey',
                    zIndex: -1,
                },
            }}
        >
            <ListItemIcon
                sx={{
                    minWidth: 'auto',
                    justifyContent: 'center',
                    height: '2rem',
                    position: 'relative',
                    zIndex: 1,
                }}
            >
                {(isFirst || isLast) && <FiberManualRecordIcon sx={{ fontSize: '2rem', color: !isPassed ? color : 'lightgrey' }} />}
                {!(isFirst || isLast) && <FiberManualRecordOutlinedIcon sx={{ fontSize: '2rem', color: !isPassed ? color : 'lightgrey' }} />}
            </ListItemIcon>
            <ListItemButton
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    height: (isFirst || isLast) ? '4rem' : '3rem',
                    paddingTop: 0,
                    paddingBottom: 0,
                }}
                onClick={() => clickHandler(gtfsId)}
            >
                <ListItemText
                    primary={<Typography sx={{ fontWeight: 'bold', opacity: isPassed ? 0.3 : 1 }}>{stop.name}</Typography>}
                />
                <ListItemText
                    sx={{ textAlign: 'right', opacity: isPassed ? 0.3 : 1, color: realtimeStatus === 'CANCELED' ? 'red' : realtimeStatus === 'UPDATED' ? theme.palette.success.main : theme.palette.text.primary }}
                    primary={(
                        <>
                            {realtimeStatus === 'CANCELED' && (
                                <ErrorOutlineIcon sx={{ color: 'red', marginRight: '5px', fontSize: '1rem' }} />
                            )}
                            {realtimeStatus === 'UPDATED' && (
                                <SensorsIcon sx={{ marginRight: '5px', fontSize: '1rem', animation: 'blink 2s linear infinite', color: theme.palette.success.main }} />
                            )}
                            {realtimeStatus !== 'CANCELED' && realtimeStatus !== 'UPDATED' && (
                                <DepartureBoardIcon sx={{ marginRight: '5px', fontSize: '1rem' }} />
                            )}
                            <span>
                                {isToday
                                    ? formatTime(arrivalRealtime, departureRealtime, timeFormatPreference, t)
                                    : formatAbsoluteStopTimeWindow(arrivalRealtime, departureRealtime)}
                            </span>
                        </>
                    )}
                    secondary={formatDelay(delay || 0)}
                    secondaryTypographyProps={{
                        sx: {
                            color: (delay ?? 0) >= 30
                                ? 'error.main'
                                : (delay ?? 0) <= -30
                                        ? 'warning.main'
                                        : 'text.secondary',
                        },
                    }}
                />
            </ListItemButton>
        </Box>
    )
}

const BusTracker = ({ tripData, isToday }: BusTrackerProps) => {
    const { stop_times } = tripData
    const [timeDisplayPreference] = useLocalStorage<FormatType>('timeDisplayPreference', 'relative')
    const arrivalRefs = useRef<(HTMLDivElement | null)[]>([])
    const scrollRef = useRef<boolean>(false)

    const scrollToFirstArrival = useCallback(() => {
        if (!isToday) return
        if (scrollRef.current) {
            return
        }
        const firstArrivalIndex = stop_times.findIndex(stopTime => figureOutIfBusHasPassed(stopTime, tripData, isToday) === false)
        if (firstArrivalIndex !== -1 && firstArrivalIndex !== 0) {
            const targetRef = arrivalRefs.current[firstArrivalIndex > 1 ? firstArrivalIndex - 2 : firstArrivalIndex > 1 ? firstArrivalIndex - 1 : firstArrivalIndex]
            if (targetRef) {
                targetRef.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                })
            }
            scrollRef.current = true
        }
    }, [isToday, tripData, stop_times])

    useEffect(() => {
        arrivalRefs.current = []
    }, [])

    useEffect(() => {
        scrollToFirstArrival()
    }, [scrollToFirstArrival])

    return (
        <List sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            {stop_times.map((stopTime, index) => (
                <BusStop
                    key={stopTime.stop.gtfs_id}
                    gtfsId={stopTime.stop.gtfs_id}
                    stop={stopTime.stop}
                    sequence={stopTime.sequence}
                    realtime={stopTime.realtime}
                    realtimeStatus={stopTime.realtime_status}
                    arrivalRealtime={stopTime.arrival_realtime}
                    departureRealtime={stopTime.departure_realtime}
                    delay={stopTime.arrival_delay}
                    color={tripData.color}
                    isFirst={index === 0}
                    isLast={index === stop_times.length - 1}
                    isPassed={figureOutIfBusHasPassed(stopTime, tripData, isToday)}
                    isToday={isToday}
                    timeFormatPreference={timeDisplayPreference}
                    stopRef={(el) => {
                        if (el) arrivalRefs.current[index] = el
                    }}
                />
            ))}
        </List>
    )
}

const TripArrivals = ({ tripData, isToday = true }: TripArrivalsProps) => {
    return <BusTracker tripData={tripData} isToday={isToday} />
}

export default TripArrivals
